import { FilePlus2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PackagingItem } from '@/shared/hooks/api/packagingSuppliers'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { evaluatePackagingItemDeclarationOfConformityRequirements } from './lib/packagingItemDeclarationOfConformityRequirements'
import { PackagingItemDeclarationOfConformityExportDialog } from './PackagingItemDeclarationOfConformityExportDialog'

type PackagingItemDeclarationOfConformityExportButtonProps = {
  packagingSupplierId: string
  item: PackagingItem
}

export function PackagingItemDeclarationOfConformityExportButton({
  packagingSupplierId,
  item,
}: PackagingItemDeclarationOfConformityExportButtonProps) {
  const { t } = useTranslation('packagingSuppliers')
  const canWriteOrgData = useCanWriteOrgData()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [progressMessage, setProgressMessage] = useState<string | null>(null)

  const { exportReady } =
    evaluatePackagingItemDeclarationOfConformityRequirements(item)

  const isGenerating = progressMessage != null
  const showReadOnlyTooltip = !canWriteOrgData
  const showRequirementsTooltip = canWriteOrgData && !exportReady

  let buttonLabel = t('itemDetail.declarationOfConformity.export')
  if (isGenerating && progressMessage) {
    buttonLabel = progressMessage
  }
  if (isGenerating && !progressMessage) {
    buttonLabel = t('itemDetail.declarationOfConformity.exporting')
  }

  const button = (
    <Button
      type='button'
      variant='outline'
      size='sm'
      disabled={!canWriteOrgData || !exportReady || isGenerating}
      onClick={() => {
        if (!canWriteOrgData || !exportReady) {
          return
        }
        setDialogOpen(true)
      }}
    >
      {isGenerating ? (
        <Loader2 className='size-3.5 animate-spin' />
      ) : (
        <FilePlus2 className='size-3.5' />
      )}
      {buttonLabel}
    </Button>
  )

  let buttonWithHint = button
  if (showReadOnlyTooltip) {
    buttonWithHint = (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex' tabIndex={0}>
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent side='bottom' align='end' className='max-w-sm'>
          <p>{t('itemDetail.declarationOfConformity.readOnlyHint')}</p>
        </TooltipContent>
      </Tooltip>
    )
  }
  if (showRequirementsTooltip) {
    buttonWithHint = (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex' tabIndex={0}>
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent side='bottom' align='end' className='max-w-sm'>
          <p>{t('itemDetail.declarationOfConformity.requirementsHint')}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      {buttonWithHint}
      <PackagingItemDeclarationOfConformityExportDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        packagingSupplierId={packagingSupplierId}
        itemId={item.id}
        onProgressMessage={setProgressMessage}
      />
    </>
  )
}
